import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Alert
} from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { COUNTRIES, getAddressConfig } from '../addressConfig';
import { validatePassword } from '../passwordValidator';

interface AddVendorDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddVendorDialog({ open, onClose, onSuccess }: AddVendorDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactFirstName: '',
    contactLastName: '',
    loginEmail: '',
    loginPassword: '',
    loginPasswordConfirm: '',
    email: '',
    phone: '',
    country: 'US',
    address: '',
    city: '',
    state: '',
    zip: '',
    taxId: '',
    revenueSharePercent: '',
    contractStartDate: '',

    // NMI
    nmiSecurityKey: '',
    nmiTokenizationKey: '',
    nmiAccountStatus: 'not_connected',
    nmiPercentageFee: '',
    nmiFixedFee: '',
    
    payoutSchedule: 'monthly',
    payoutCurrency: 'USD',
    status: 'active'
  });

  const handleSubmit = async () => {
    // Client-side validation. The server re-validates these, but checking
    // here gives instant feedback before a network round-trip.
    if (!formData.companyName.trim()) {
      alert('Company name is required.');
      return;
    }
    if (!formData.loginEmail) {
      alert('Login email is required to create the partner account.');
      return;
    }
    // Same policy as partner self-signup and Change Password — this form
    // used to accept 8 characters, which was the weakest door into the app.
    const passwordCheck = validatePassword(formData.loginPassword);
    if (!passwordCheck.isValid) {
      alert('Password does not meet requirements:\n\n\u2022 ' + passwordCheck.errors.join('\n\u2022 '));
      return;
    }
    if (formData.loginPassword !== formData.loginPasswordConfirm) {
      alert('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const functions = getFunctions();
      const createVendor = httpsCallable(functions, 'createVendor');
      const result = await createVendor({ ...formData, preferredPaymentGateway: 'nmi' });
      const data = result.data as { loginEmail: string };

      alert(
        `Partner "${formData.companyName}" created successfully!\n\n` +
        `Login: ${data.loginEmail}\nRole: partner (auto-assigned)`
      );

      setFormData({
        companyName: '',
        contactFirstName: '',
        contactLastName: '',
        loginEmail: '',
        loginPassword: '',
        loginPasswordConfirm: '',
        email: '',
        phone: '',
        country: 'US',
        address: '',
        city: '',
        state: '',
        zip: '',
        taxId: '',
        revenueSharePercent: '',
        contractStartDate: '',
        nmiSecurityKey: '',
        nmiTokenizationKey: '',
        nmiAccountStatus: 'not_connected',
        nmiPercentageFee: '',
        nmiFixedFee: '',
        payoutSchedule: 'monthly',
        payoutCurrency: 'USD',
        status: 'active'
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adding vendor:', error);
      alert('Failed to add partner: ' + (error as Error).message);
      // Dialog stays open so the admin can correct and retry. Server-side
      // rollback ensures no orphan auth user / vendor doc was left behind.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Partner</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          
          {/* Company Info */}
          <Typography variant="h6" sx={{ mt: 1 }}>Company Information</Typography>
          <Divider />
          
          <TextField
            fullWidth
            label="Company Name"
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            required
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Contact First Name"
              value={formData.contactFirstName}
              onChange={(e) => setFormData({ ...formData, contactFirstName: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Contact Last Name"
              value={formData.contactLastName}
              onChange={(e) => setFormData({ ...formData, contactLastName: e.target.value })}
              required
            />
          </Box>
          
          {/* Contact Info */}
          <Typography variant="h6" sx={{ mt: 2 }}>Contact Information</Typography>
          <Divider />
          
          <Typography variant="h6" sx={{ mt: 2 }}>Partner Login Account</Typography>
          <Divider />

          <Alert severity="info">
            A Firebase Auth account will be created automatically. The partner can log in to the Partner Dashboard with these credentials.
          </Alert>

          <TextField
            fullWidth
            label="Login Email"
            type="email"
            value={formData.loginEmail}
            onChange={(e) => setFormData({ ...formData, loginEmail: e.target.value })}
            required
            helperText="Email for partner dashboard login"
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={formData.loginPassword}
            onChange={(e) => setFormData({ ...formData, loginPassword: e.target.value })}
            required
            helperText="Minimum 12 characters, with uppercase, lowercase, a number and a special character"
            autoComplete="new-password"
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={formData.loginPasswordConfirm}
            onChange={(e) => setFormData({ ...formData, loginPasswordConfirm: e.target.value })}
            required
            autoComplete="new-password"
            error={formData.loginPasswordConfirm.length > 0 && formData.loginPassword !== formData.loginPasswordConfirm}
            helperText={
              formData.loginPasswordConfirm.length > 0 && formData.loginPassword !== formData.loginPasswordConfirm
                ? 'Passwords do not match'
                : ''
            }
          />

          {/* Contact Info */}
          <Typography variant="h6" sx={{ mt: 2 }}>Contact Information</Typography>
          <Divider />

          <TextField
            fullWidth
            label="Contact Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            helperText="Business/support email for customers (can be same as login email)"
            required
          />
          
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            required
          />
          
          {/* Address */}
          <Typography variant="h6" sx={{ mt: 2 }}>Address</Typography>
          <Divider />

          <FormControl fullWidth required>
            <InputLabel>Country</InputLabel>
            <Select
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value, state: '', zip: '' })}
              label="Country"
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {(() => {
            const addrCfg = getAddressConfig(formData.country);
            return (
              <>
                {addrCfg.postalFirst && addrCfg.postalLabel && (
                  <TextField
                    fullWidth
                    label={addrCfg.postalLabel}
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    helperText={addrCfg.postalFormat}
                    slotProps={{ htmlInput: { maxLength: addrCfg.postalMaxLength } }}
                  />
                )}

                {addrCfg.regionOptions ? (
                  <FormControl fullWidth>
                    <InputLabel>{addrCfg.regionLabel}</InputLabel>
                    <Select
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      label={addrCfg.regionLabel}
                    >
                      <MenuItem value=""><em>Select {addrCfg.regionLabel}</em></MenuItem>
                      {addrCfg.regionOptions.map((r) => (
                        <MenuItem key={r} value={r}>{r}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label={addrCfg.regionLabel}
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                )}

                <TextField
                  fullWidth
                  label={addrCfg.cityLabel}
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />

                <TextField
                  fullWidth
                  label={addrCfg.streetLabel}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />

                {!addrCfg.postalFirst && addrCfg.postalLabel && (
                  <TextField
                    fullWidth
                    label={addrCfg.postalLabel}
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    helperText={addrCfg.postalFormat}
                    slotProps={{ htmlInput: { maxLength: addrCfg.postalMaxLength } }}
                  />
                )}
              </>
            );
          })()}

          {/* Business Info */}
          <Typography variant="h6" sx={{ mt: 2 }}>Business Information</Typography>
          <Divider />
          
          <TextField
            fullWidth
            label="Tax ID / EIN"
            value={formData.taxId}
            onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
            helperText="For tax reporting and 1099s"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Revenue Share %"
              type="number"
              value={formData.revenueSharePercent}
              onChange={(e) => setFormData({ ...formData, revenueSharePercent: e.target.value })}
              helperText="Partner's percentage of revenue"
              slotProps={{ htmlInput: { min: 0, max: 100, step: 0.1 } }}
              sx={{ flex: 1 }}
            />

            <TextField
              label="Contract Start Date"
              type="date"
              value={formData.contractStartDate}
              onChange={(e) => setFormData({ ...formData, contractStartDate: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Payment Information */}
          <Typography variant="h6" sx={{ mt: 2 }}>Payment Information</Typography>
          <Divider />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Payout Schedule</InputLabel>
              <Select
                value={formData.payoutSchedule}
                onChange={(e) => setFormData({ ...formData, payoutSchedule: e.target.value })}
                label="Payout Schedule"
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Payout Currency</InputLabel>
              <Select
                value={formData.payoutCurrency}
                onChange={(e) => setFormData({ ...formData, payoutCurrency: e.target.value })}
                label="Payout Currency"
              >
                <MenuItem value="USD">USD - US Dollar</MenuItem>
                <MenuItem value="CAD">CAD - Canadian Dollar</MenuItem>
                <MenuItem value="EUR">EUR - Euro</MenuItem>
                <MenuItem value="GBP">GBP - British Pound</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* NMI Payment Gateway */}
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>NMI Payment Gateway</Typography>

          <TextField
            fullWidth
            label="NMI Private Security Key"
            value={formData.nmiSecurityKey}
            onChange={(e) => setFormData({ ...formData, nmiSecurityKey: e.target.value })}
            helperText="Server-side API security key — stored encrypted. Routes charges to partner's merchant account."
          />

          <TextField
            fullWidth
            label="NMI Public Tokenization Key (Collect.js)"
            value={formData.nmiTokenizationKey}
            onChange={(e) => setFormData({ ...formData, nmiTokenizationKey: e.target.value })}
            helperText="Client-side Collect.js key — public by design. Must come from the same NMI merchant account as the security key above."
          />

          <FormControl fullWidth>
            <InputLabel>NMI Account Status</InputLabel>
            <Select
              value={formData.nmiAccountStatus}
              onChange={(e) => setFormData({ ...formData, nmiAccountStatus: e.target.value })}
              label="NMI Account Status"
            >
              <MenuItem value="not_connected">Not Connected</MenuItem>
              <MenuItem value="pending">Pending Verification</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>

          <Alert severity="info">
            Custom Fee Structure (optional - leave blank to use system defaults)
          </Alert>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="NMI Percentage Fee"
              type="number"
              value={formData.nmiPercentageFee}
              onChange={(e) => setFormData({ ...formData, nmiPercentageFee: e.target.value })}
              helperText="e.g., 0.025 for 2.5%"
              slotProps={{ htmlInput: { step: "0.001", min: "0", max: "1" } }}
              sx={{ flex: 1 }}
            />

            <TextField
              label="NMI Fixed Fee ($)"
              type="number"
              value={formData.nmiFixedFee}
              onChange={(e) => setFormData({ ...formData, nmiFixedFee: e.target.value })}
              helperText="e.g., 0.25"
              slotProps={{ htmlInput: { step: "0.01", min: "0" } }}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Partner Status */}
          <FormControl fullWidth required>
            <InputLabel>Partner Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              label="Partner Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Creating...' : 'Add Partner'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
